import { useTranslation } from "react-i18next";
import { FX_PAIRS, normalizeRate, type SettingsDto } from "@hisense/shared";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { formatDateTime } from "@/domain/datetime";

/**
 * Tiền tệ theo công ty + tỷ giá quy đổi đơn giá sản phẩm (yêu cầu v1.8 mục 6.2, 6.3; 11/10/2026).
 *
 * Mỗi cặp một dòng ghi đủ chiều: "1 USD = [6.6] RMB", ngay dưới là chiều ngược suy ra ("1 RMB = 0.151515 USD") để người nhập thấy cả
 * hai hướng mà không phải nhập hai con số lệch nhau. Để trống = chưa có tỷ giá: quy đổi cặp đó bị chặn kèm lời nhắc đặt tỷ giá.
 */
export function CurrencyRatesPanel({
  draft,
  saved,
  onChange,
  disabled,
}: {
  draft: SettingsDto;
  /** Cài đặt đang lưu — để cảnh báo khi tỷ giá vừa gõ lệch mạnh (gõ nhầm dấu chấm nghìn "25.400" thành 25.4). */
  saved?: SettingsDto;
  onChange: (next: SettingsDto) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const currencies = [
    ...new Set([...(draft.businessOperations.optionLists.currencies ?? []), "USD", "HKD", "RMB", "VND"]),
  ];
  const rateOf = (base: string, quote: string) =>
    draft.businessOperations.fxRates.find((r) => r.base === base && r.quote === quote);

  const setRate = (base: (typeof FX_PAIRS)[number][0], quote: (typeof FX_PAIRS)[number][1], value: string) => {
    const others = draft.businessOperations.fxRates.filter((r) => !(r.base === base && r.quote === quote));
    const kept = value.trim() === "" ? others : [...others, { base, quote, rate: value, updatedAt: rateOf(base, quote)?.updatedAt ?? null }];
    onChange({ ...draft, businessOperations: { ...draft.businessOperations, fxRates: kept } });
  };

  return (
    <div className="space-y-5 p-4">
      <div>
        <h3 className="text-sm font-semibold">{t("fxSettings.regionTitle")}</h3>
        <p className="mt-1 text-xs text-ink-soft">{t("fxSettings.regionHint")}</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {(["vietnam", "china"] as const).map((region) => (
            <div key={region}>
              <FieldLabel htmlFor={`fx-default-${region}`}>{t(`fxSettings.default_${region}`)}</FieldLabel>
              <Select
                id={`fx-default-${region}`}
                disabled={disabled}
                value={draft.system.defaultCurrencyByRegion[region]}
                onChange={(e) =>
                  onChange({
                    ...draft,
                    system: {
                      ...draft.system,
                      defaultCurrencyByRegion: { ...draft.system.defaultCurrencyByRegion, [region]: e.target.value },
                    },
                  })
                }
              >
                {[...new Set([...currencies, draft.system.defaultCurrencyByRegion[region]])].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold">{t("fxSettings.ratesTitle")}</h3>
        <p className="mt-1 text-xs text-ink-soft">{t("fxSettings.ratesHint")}</p>
        <div className="mt-3 divide-y divide-line rounded-sm border border-line">
          {FX_PAIRS.map(([base, quote]) => {
            const current = rateOf(base, quote);
            const raw = current?.rate ?? "";
            const valid = raw === "" || normalizeRate(raw) !== null;
            const reverse = valid && raw ? reverseLabel(raw) : null;
            const savedRate = saved?.businessOperations.fxRates.find((r) => r.base === base && r.quote === quote)?.rate;
            const drift = valid && raw && savedRate ? Math.abs(Number(normalizeRate(raw)) / Number(normalizeRate(savedRate)) - 1) : 0;
            return (
              <div key={`${base}-${quote}`} className="grid items-center gap-2 px-3 py-2 sm:grid-cols-[1fr_auto]">
                <div className="flex flex-wrap items-center gap-2 font-mono tabular text-sm">
                  <span className="text-ink">1 {base} =</span>
                  <Input
                    aria-label={t("fxSettings.rateAria", { base, quote })}
                    className="w-40 text-right font-mono"
                    inputMode="decimal"
                    disabled={disabled}
                    value={raw}
                    placeholder={t("fxSettings.notSet")}
                    onChange={(e) => setRate(base, quote, e.target.value)}
                  />
                  <span className="text-ink">{quote}</span>
                  {!valid && <span role="alert" className="font-sans text-xs text-danger">{t("fxSettings.invalid")}</span>}
                  {drift > 0.2 && <span role="alert" className="font-sans text-xs text-warn">{t("fxSettings.bigChange", { saved: savedRate })}</span>}
                </div>
                <div className="text-right text-2xs text-ink-soft">
                  {reverse ? <span className="font-mono tabular">1 {quote} = {reverse} {base}</span> : <span>{t("fxSettings.notSetHint")}</span>}
                  {current?.updatedAt ? <div>{t("fxSettings.updatedAt", { at: formatDateTime(current.updatedAt) })}</div> : null}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-ink-soft">{t("fxSettings.savedPiNote")}</p>
      </div>
    </div>
  );
}

/** Chiều ngược, chỉ để đọc: 1 ÷ tỷ giá, 6 chữ số có nghĩa, không ký hiệu mũ (1 VND = 0.0000393701 USD). */
function reverseLabel(rate: string): string | null {
  const n = Number(normalizeRate(rate));
  if (!Number.isFinite(n) || n <= 0) return null;
  const r = 1 / n;
  const decimals = Math.min(12, Math.max(0, 5 - Math.floor(Math.log10(r))));
  return r.toFixed(decimals).replace(/\.?0+$/, "");
}
